import { useState } from 'react'
import { Box, Flex, Text, VStack } from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import { LuFootprints as Footprints } from 'react-icons/lu'
import { DataTable, DataList, PageHeader } from '@minetrace/ui'
import { brand, themeColors } from '../../theme'
import { shipmentsService } from '../../api/services/shipments.service'
import { usePagedRequest } from '../../hooks'
import type { ShipmentListItemResponse } from '../../api/models/response'
import { parseUtcDate } from '../../lib/dates'
import { formatCarbonKg, formatCarbonKgShort } from '../../lib/carbonFootprint'
import { resolveUbigeoLabel } from '../../lib/ubigeo'
import { PauseBadge } from '../../components/PauseBadge'
import { useT } from '../../i18n'
import { exportToExcel, type ExcelColumn } from '../../utils/exportExcel'
import { getShipmentState } from '../../lib/shipmentStates'
import { ShipmentEventsModal } from './ShipmentEventsModal'
import { ForceShipmentStateModal } from './ForceShipmentStateModal'

const formatDateTime = (iso: string | null) => {
  if (!iso) return '-'
  const d = parseUtcDate(iso)
  return (
    d.toLocaleDateString('es-PE', { day: '2-digit', month: 'short' }) +
    ' ' +
    d.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })
  )
}

const excelColumns: ExcelColumn<ShipmentListItemResponse>[] = [
  { header: 'Lote', accessor: (s) => s.batchCode },
  { header: 'Conductor', accessor: (s) => s.driverName ?? '-' },
  { header: 'Placa', accessor: (s) => s.vehiclePlate ?? '-' },
  { header: 'Mineral', accessor: (s) => s.mineralTypeName ?? '-' },
  { header: 'Peso', accessor: (s) => s.mineralWeight ? `${s.mineralWeight} ${s.measurementUnitName ?? ''}`.trim() : '-' },
  { header: 'Huella CO₂', accessor: (s) => formatCarbonKg(s.carbonKg) },
  { header: 'Origen', accessor: (s) => {
    const name = s.miningRightName ?? ''
    const ubigeo = resolveUbigeoLabel(s.miningRightUbigeoCode)
    return [name, ubigeo].filter(Boolean).join(' — ') || '-'
  } },
  { header: 'Inicio', accessor: (s) => formatDateTime(s.startedAt) },
  { header: 'Fin', accessor: (s) => formatDateTime(s.completedAt) },
  { header: 'Estado', accessor: (s) => getShipmentState(s.shipmentState).label },
]

export default function TrasladosPage() {
  const colors = themeColors
  const navigate = useNavigate()
  const { t } = useT()
  const [search, setSearch] = useState('')
  const [eventsModalShipment, setEventsModalShipment] = useState<{ id: string; batchCode: string } | null>(null)
  const [forceStateShipment, setForceStateShipment] = useState<{ id: string; batchCode: string; currentState: number } | null>(null)

  const {
    items: shipments,
    totalCount,
    isFetching: loading,
    error,
    refresh,
    page,
    setPage,
    totalPages,
    pageSize,
    setPageSize,
  } = usePagedRequest(shipmentsService.getAll, {
    params: { search: search.trim() || undefined },
  })

  return (
    <VStack gap={5} alignItems="stretch">
      <PageHeader
        title={t('traslados.title')}
        subtitle={t('traslados.subtitle')}
        colors={colors}
      />

      <DataList<ShipmentListItemResponse>
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar por lote, conductor, placa, proveedor, mineral..."
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        totalCount={totalCount}
        entityName="traslados"
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        onExport={() => exportToExcel(shipments, excelColumns, 'traslados')}
        brandColor={brand.green}
        headers={['Lote', 'Conductor', 'Placa', 'Mineral', 'Peso', 'Huella CO₂', 'Origen', 'Inicio', 'Fin', 'Estado']}
        data={shipments}
        loading={loading}
        error={error}
        emptyMessage="No se encontraron traslados"
        loadingMessage="Cargando traslados..."
        colors={colors}
        keyExtractor={(s) => s.id}
        renderRow={(shipment) => {
          const stateInfo = getShipmentState(shipment.shipmentState)
          return (
            <>
              {/* Lote */}
              <DataTable.Cell>
                <Text whiteSpace="nowrap" fontFamily="'JetBrains Mono', monospace" fontSize="sm" fontWeight="600" color={colors.text}>
                  {shipment.batchCode}
                </Text>
              </DataTable.Cell>

              {/* Conductor */}
              <DataTable.Cell>
                <Text fontSize="sm" color={colors.text} whiteSpace="nowrap">
                  {shipment.driverName || '-'}
                </Text>
              </DataTable.Cell>

              {/* Placa */}
              <DataTable.Cell>
                <Text fontSize="sm" fontWeight="600" color={colors.text}>
                  {shipment.vehiclePlate || '-'}
                </Text>
              </DataTable.Cell>

              {/* Mineral */}
              <DataTable.Cell>
                <Text fontSize="sm" fontWeight="500" color={brand.green}>
                  {shipment.mineralTypeName || '-'}
                </Text>
              </DataTable.Cell>

              {/* Peso */}
              <DataTable.Cell>
                <Text fontSize="sm" color={colors.text}>
                  {shipment.mineralWeight ? `${shipment.mineralWeight} ${shipment.measurementUnitName ?? ''}`.trim() : '-'}
                </Text>
              </DataTable.Cell>

              {/* Huella de Carbono (real, sellada; "—" si aún no se calcula) */}
              <DataTable.Cell>
                <Flex alignItems="center" gap={1} whiteSpace="nowrap">
                  <Footprints size={13} color={shipment.carbonKg != null ? brand.green : colors.textDimmed} />
                  <Text fontSize="sm" fontWeight="500" color={shipment.carbonKg != null ? brand.green : colors.textDimmed}>
                    {formatCarbonKgShort(shipment.carbonKg)}
                  </Text>
                </Flex>
              </DataTable.Cell>

              {/* Origen */}
              <DataTable.Cell>
                <VStack align="start" gap={0}>
                  <Text fontSize="sm" color={colors.text} whiteSpace="nowrap">
                    {shipment.miningRightName ?? '-'}
                  </Text>
                  <Text fontSize="xs" color={colors.textMuted} whiteSpace="nowrap">
                    {resolveUbigeoLabel(shipment.miningRightUbigeoCode)}
                  </Text>
                </VStack>
              </DataTable.Cell>

              {/* Inicio */}
              <DataTable.Cell>
                <Text fontSize="xs" color={colors.textMuted} whiteSpace="nowrap">
                  {formatDateTime(shipment.startedAt)}
                </Text>
              </DataTable.Cell>

              {/* Fin */}
              <DataTable.Cell>
                <Text fontSize="xs" color={colors.textMuted} whiteSpace="nowrap">
                  {formatDateTime(shipment.completedAt)}
                </Text>
              </DataTable.Cell>

              {/* Estado (+ chip de pausa si el traslado está pausado) */}
              <DataTable.Cell>
                <Flex alignItems="center" gap={1.5} flexWrap="wrap">
                  <Box
                    display="inline-block"
                    px={2.5}
                    py={1}
                    borderRadius="full"
                    fontSize="10px"
                    fontWeight="700"
                    textTransform="uppercase"
                    letterSpacing="0.06em"
                    bg={`${stateInfo.color}33`}
                    color={stateInfo.color}
                  >
                    {stateInfo.label}
                  </Box>
                  {shipment.isPaused && <PauseBadge reason={shipment.pauseReason} />}
                </Flex>
              </DataTable.Cell>
            </>
          )
        }}
        actions={(shipment) => [
          {
            label: 'Ver Recorrido',
            onClick: () => navigate(`/app/traslados/${shipment.id}`),
          },
          {
            label: 'Ver Eventos',
            onClick: () => setEventsModalShipment({ id: shipment.id, batchCode: shipment.batchCode }),
          },
          {
            label: 'Cambiar estado (dev)',
            onClick: () => setForceStateShipment({
              id: shipment.id,
              batchCode: shipment.batchCode,
              currentState: shipment.shipmentState,
            }),
          },
        ]}
      />

      <ShipmentEventsModal
        shipmentId={eventsModalShipment?.id ?? null}
        batchCode={eventsModalShipment?.batchCode}
        onClose={() => setEventsModalShipment(null)}
      />

      <ForceShipmentStateModal
        shipmentId={forceStateShipment?.id ?? null}
        batchCode={forceStateShipment?.batchCode}
        currentState={forceStateShipment?.currentState}
        onClose={() => setForceStateShipment(null)}
        onChanged={refresh}
      />
    </VStack>
  )
}
